import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Power } from "lucide-react";
import { updateBuilding, type Building } from "@/lib/api/building";
import { SectionPanel } from "@/components/ui/section-panel";
import { cn } from "@/lib/utils";

// Automaattinen optimointi -kytkin — enables the autonomous loop within the strategy bounds.
// Belongs to the Optimointi (loop) tier.
export function LiveOptimizationToggle({
  building,
  operator,
  onUpdated,
}: {
  building: Building;
  operator: boolean;
  onUpdated: (building: Building) => void;
}) {
  const [saving, setSaving] = useState(false);
  const enabled = building.live_optimization_enabled;

  async function toggle() {
    setSaving(true);
    try {
      const updated = await updateBuilding(building.id, {
        live_optimization_enabled: !enabled,
      });
      onUpdated(updated);
      toast.success(
        !enabled
          ? "Automaattinen optimointi käytössä"
          : "Automaattinen optimointi pois päältä",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Päivitys epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionPanel
      icon={Power}
      title="Automaattinen optimointi"
      className="mt-6"
    >
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground max-w-prose">
          Kun käytössä, Kairos soveltaa optimointireseptit automaattisesti
          strategian rajojen sisällä — ilman erillistä hyväksyntää. Rajat
          asetetaan strategiassa ja niitä muutetaan vain strategiaehdotusten
          kautta.
        </p>
        <button
          type="button"
          onClick={toggle}
          disabled={!operator || saving}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50",
            enabled
              ? "bg-success-subtle text-success-subtle-foreground hover:bg-success/20"
              : "bg-muted text-muted-foreground hover:bg-muted/70",
          )}
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Power className="h-3.5 w-3.5" />
          )}
          {enabled ? "Käytössä" : "Pois päältä"}
        </button>
      </div>
    </SectionPanel>
  );
}
